/**
 * Shrinking a photo to print size before the PDF printer embeds it.
 *
 * Chrome passes a JPEG straight into a PDF when it can, but a photo it cannot
 * (a PNG, or a JPEG it had to turn upright) goes in losslessly at full size:
 * five phone photos made a 100 MB PDF. Redrawn as a JPEG at print size, each
 * is a few hundred kilobytes and passes straight through.
 */

/**
 * The longest edge, in pixels. The cover photo is the widest thing printed,
 * 8.5 inches across; this is about 190 dots per inch there, and far more for
 * a finding's photo.
 */
const PRINT_MAX_EDGE = 1600

const PRINT_QUALITY = 0.82

/** The photo as a print-sized JPEG, or null when this browser cannot make one. */
export async function shrinkForPrint(image: HTMLImageElement): Promise<Blob | null> {
  const { naturalWidth, naturalHeight } = image
  if (naturalWidth === 0 || naturalHeight === 0) return null
  const scale = Math.min(1, PRINT_MAX_EDGE / Math.max(naturalWidth, naturalHeight))
  const canvas = window.document.createElement('canvas')
  canvas.width = Math.round(naturalWidth * scale)
  canvas.height = Math.round(naturalHeight * scale)
  const context = canvas.getContext('2d')
  if (context === null) return null
  try {
    // Drawn upright: the browser applies the photo's orientation, and the
    // JPEG made from it carries none, so nothing turns it again.
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
  } catch {
    // An image the browser will not let us read, served without CORS.
    return null
  }
  return new Promise((resolve) => {
    try {
      canvas.toBlob(resolve, 'image/jpeg', PRINT_QUALITY)
    } catch {
      resolve(null)
    }
  })
}
